"use client";

import { ArrowRight, Check } from "lucide-react";

import { FadeIn, StaggerContainer, StaggerItem } from "@/components/ui/motion";
import { useLang } from "@/contexts/lang";
import type { SectionIds } from "@/data/home";

type PartnersSectionProps = {
	sectionIds: SectionIds;
};

export function PartnersSection({ sectionIds }: PartnersSectionProps) {
	const t = useLang();

	return (
		<section
			id={sectionIds.partners}
			className="scroll-mt-32 bg-cream pt-20 sm:pt-24 md:pt-32 pb-8 sm:pb-12 mt-paper overflow-hidden"
		>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="grid gap-12 md:gap-16 lg:gap-20 lg:grid-cols-12 lg:items-start">
					{/* Left column */}
					<div className="lg:col-span-5 space-y-8 md:space-y-10">
						<FadeIn direction="right">
							<div className="lg:sticky lg:top-32">
								{/* Refined label */}
								<div className="flex items-center gap-4 mb-6 md:mb-8">
									<span className="h-px w-10 sm:w-16 bg-accent" />
									<p className="text-label text-accent">{t.partners.label}</p>
								</div>

								<h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-primary leading-[1.1] tracking-tight mb-5 md:mb-6">
									{t.partners.heading1}
									<span className="block mt-2">
										<span className="text-accent">{t.partners.heading2}</span>
									</span>
								</h2>

								<p className="text-refined text-base sm:text-lg text-foreground/70 mb-8 md:mb-10 max-w-md">
									{t.partners.intro}
								</p>
							</div>
						</FadeIn>
					</div>

					{/* Right column */}
					<div className="lg:col-span-7">
						<FadeIn direction="left" delay={0.1}>
							<div className="relative rounded-sm bg-white p-6 sm:p-8 md:p-10 shadow-luxury overflow-hidden">
								{/* Subtle ambient glow */}
								<div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-50 pointer-events-none bg-accent/5 blur-3xl -translate-y-1/2 translate-x-1/2" />

								{/* Decorative corner */}
								<div className="absolute top-0 right-0 w-16 h-16 pointer-events-none">
									<svg
										viewBox="0 0 64 64"
										fill="none"
										className="w-full h-full"
										aria-hidden="true"
									>
										<path
											d="M64 0 L64 64 L0 64"
											stroke="rgba(201, 161, 90, 0.25)"
											strokeWidth="1"
											fill="none"
										/>
									</svg>
								</div>

								<div className="relative z-10">
									{/* Header */}
									<div className="mb-8 md:mb-10">
										<div className="flex items-center gap-4 mb-4">
											<span className="h-px w-8 bg-accent/40" />
											<p className="text-label text-accent/70">
												{t.partners.corporateServicesLabel}
											</p>
										</div>
										<p className="font-display text-2xl sm:text-3xl font-normal text-primary tracking-tight">
											{t.partners.whyUs}
										</p>
									</div>

									{/* Benefits list */}
									<StaggerContainer
										staggerDelay={0.06}
										delayStart={0.2}
										className="grid gap-3 sm:gap-4 sm:grid-cols-2"
									>
										{t.partners.benefits.map((benefit) => (
											<StaggerItem key={benefit}>
												<div className="group flex items-start gap-3 bg-cream/80 rounded-sm p-4 border border-primary/[0.03] transition-all duration-500 hover:bg-secondary/50 hover:shadow-luxury-sm">
													<span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-accent/10 transition-colors group-hover:bg-accent/20">
														<Check className="h-3 w-3 text-accent" />
													</span>
													<span className="text-sm text-foreground/90 leading-relaxed">
														{benefit}
													</span>
												</div>
											</StaggerItem>
										))}
									</StaggerContainer>

									{/* CTA card */}
									<FadeIn delay={0.5}>
										<div className="mt-8 md:mt-10 rounded-sm bg-primary p-6 sm:p-8 shadow-luxury">
											<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
												<div>
													<p className="font-display text-lg sm:text-xl font-normal text-white mb-2 tracking-tight">
														{t.partners.ctaTitle}
													</p>
													<p className="text-sm text-white/80">
														{t.partners.ctaDesc}
													</p>
												</div>
												<a
													href={`#${sectionIds.contact}`}
													className="shrink-0 inline-flex items-center justify-center gap-2.5 rounded-sm bg-accent px-6 sm:px-8 py-3 text-sm font-semibold text-primary transition-all duration-500 shadow-gold hover:shadow-gold-lg hover:bg-accent-light"
												>
													<span>{t.partners.ctaButton}</span>
													<ArrowRight className="h-4 w-4" />
												</a>
											</div>
										</div>
									</FadeIn>
								</div>
							</div>
						</FadeIn>
					</div>
				</div>
			</div>
		</section>
	);
}
